import { createFileRoute } from '@tanstack/react-router'
import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { TMDBMovie, Movie } from '../types/movie.types';
import Header from '../components/Header';
import HeroBillboard from '../components/HeroBillboard';
import HeroBillboardSkeleton from '../components/HeroBillboardSkeleton';
import MovieRow from '../components/MovieRow';
import ProgressiveMovieRow from '../components/ProgressiveMovieRow';
import CategoryRow from '../components/CategoryRow';
import MovieModal from '../components/MovieModal';
import MovieList from '../components/MovieList';
import VideoPlayer from '../components/VideoPlayer';
import LoadingOverlay from '../components/LoadingOverlay';
import TVMovieDetailsPage from '../components/TVMovieDetailsPage';
import RecentlyWatchedRow from '../components/RecentlyWatchedRow';
import SearchFilters, { FilterOptions, SortOption } from '../components/SearchFilters';
import { Skeleton } from '../components/ui/skeleton';
import { useIsTVDevice, useTVKeyboardNavigation, useTVGrid } from '../hooks/useTVNavigation';
import { useTVNavigation } from '../context/TVNavigationContext';
import { homePageMap, HOME_PAGE_ROW_ORDER, getNextRowId, getPreviousRowId } from '../navigation/maps/homePageMap';
import { getApiUrl, createApiUrl, getMDBListUrl, API_ENDPOINTS } from '../lib/api';
import { useAuthStore } from '../lib/auth';
import { buildInstantPlayUrl } from '../lib/streamSources';

// Search Result Movie Card Component
interface SearchResultMovieCardProps {
  movie: TMDBMovie;
  onClick: () => void;
  isFocused?: boolean;
  tabIndex?: number;
}

const SearchResultMovieCard = React.forwardRef<HTMLDivElement, SearchResultMovieCardProps>(
  ({ movie, onClick, isFocused, tabIndex }, ref) => {
  const [imageError, setImageError] = useState(false);
  const imageUrl = imageError ? '/placeholder-movie.png' : 
    (movie.poster_path ? `https://image.tmdb.org/t/p/w500${movie.poster_path}` : '/placeholder-movie.png');

    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: isFocused ? 1.05 : 1 }}
        transition={{ duration: 0.3 }}
        whileHover={{ y: -8, scale: 1.05 }}
        className={`cursor-pointer ${isFocused ? 'tv-focused' : ''}`}
        onClick={onClick}
        role="button"
        tabIndex={tabIndex}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
      >
      <div className="relative aspect-[2/3] bg-gray-800 rounded-lg overflow-hidden shadow-lg group">
        <img
          src={imageUrl}
          alt={movie.title}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={() => {
            if (!imageError) {
              console.error('Failed to load poster for:', movie.title);
              setImageError(true);
            }
          }}
        />
      
        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
          <div className="w-full">
            <h3 className="text-white font-semibold text-sm line-clamp-2 mb-1">
              {movie.title}
            </h3>
            <div className="flex items-center gap-2 text-xs text-gray-300">
              {movie.release_date && (
                <span>{new Date(movie.release_date).getFullYear()}</span>
              )}
              {movie.vote_average > 0 && (
                <>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <svg className="w-3 h-3 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    {movie.vote_average.toFixed(1)}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Top badge for high rated movies */}
        {movie.vote_average >= 8 && (
          <div className="absolute top-2 right-2 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded shadow-lg">
            TOP
          </div>
        )}
      </div>
    </motion.div>
    );
  }
);

SearchResultMovieCard.displayName = 'SearchResultMovieCard';


export const Route = createFileRoute('/')({
  component: Home,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      playMovie: search.playMovie as number | undefined,
      resumeFrom: search.resumeFrom as number | undefined,
    }
  }
})

function Home() {
  const { user, authenticated, loading, checkSession, getAuthHeaders } = useAuthStore();
  const isTV = useIsTVDevice();
  const { loadPageMap, focusNode, getCurrentNodeId, engine, updateNodeNeighbors } = useTVNavigation();
  const searchParams = Route.useSearch();
  const navigate = Route.useNavigate();
  
  // TMDB state - DECLARE FIRST before using
  const [trendingMovies, setTrendingMovies] = useState<TMDBMovie[]>([]);
  const [popularMovies, setPopularMovies] = useState<TMDBMovie[]>([]);
  const [heroMovie, setHeroMovie] = useState<TMDBMovie | null>(null);
  const [loadingTMDB, setLoadingTMDB] = useState(true);
  const [tmdbSearchResults, setTmdbSearchResults] = useState<TMDBMovie[]>([]);
  const [showTMDBSearch, setShowTMDBSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchProgress, setSearchProgress] = useState<{
    phase: string;
    message: string;
    progress?: number;
    total?: number;
  } | null>(null);
  
  // Filtering and sorting state
  const [filters, setFilters] = useState<FilterOptions>({
    languages: [],
    minRating: 0,
    yearRange: {}
  });
  const [sortBy, setSortBy] = useState<SortOption>('relevance');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  
  // Modal and bot search state
  const [selectedTMDBMovie, setSelectedTMDBMovie] = useState<TMDBMovie | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showTVDetailsPage, setShowTVDetailsPage] = useState(false);
  const [botResults, setBotResults] = useState<Movie[]>([]);
  const [showBotResults, setShowBotResults] = useState(false);
  
  // Streaming state
  const [selectedMovie, setSelectedMovie] = useState<Movie | null>(null);
  const [streamUrl, setStreamUrl] = useState<string | null>(null);
  const [progressCheckUrl, setProgressCheckUrl] = useState<string | null>(null);
  const [uploadingInBackground, setUploadingInBackground] = useState(false);
  const [uploadJobId, setUploadJobId] = useState<string | null>(null);
  const [streamSource, setStreamSource] = useState<'filestream' | 'gdrive'>('gdrive');
  const [fileId, setFileId] = useState<string | null>(null);
  const [resumeFromPosition, setResumeFromPosition] = useState<number | undefined>(undefined);
  
  // Dynamic lists state
  const [availableLists, setAvailableLists] = useState<any[]>([]);
  
  // Loading state
  const [isLoadingStream, setIsLoadingStream] = useState(false);
  const [showLoadingTimeout, setShowLoadingTimeout] = useState(false);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const searchAbortControllerRef = useRef<AbortController | null>(null);
  
  // Store last focused node ID and scroll position for restoration when returning from detail page
  const [lastFocusedNodeId, setLastFocusedNodeId] = useState<string | null>(null);
  const [lastScrollPosition, setLastScrollPosition] = useState<number>(0);
  
  // Instant play — navigate straight to watch page with fastest source
  const handleInstantPlay = useCallback((movie: TMDBMovie) => {
    window.location.href = buildInstantPlayUrl(movie.id, movie);
  }, []);

  // Open movie details modal / TV details page
  const handleTMDBMovieClick = useCallback((movie: TMDBMovie) => {
    if (isTV) {
      const currentNodeId = getCurrentNodeId();
      if (currentNodeId) {
        setLastFocusedNodeId(currentNodeId);
        setLastScrollPosition(window.scrollY || document.documentElement.scrollTop);
      }
    }
    
    setSelectedTMDBMovie(movie);
    if (isTV) {
      setShowTVDetailsPage(true);
    } else {
      setIsModalOpen(true);
    }
  }, [isTV, getCurrentNodeId]);
  
  // Calculate grid dimensions for search results
  const searchGridColumns = 6; // xl:grid-cols-6
  const searchCardRefs = useRef<(HTMLDivElement | null)[]>([]);
  
  // Extract available languages from results (defined early for use in filters)
  const availableLanguages = useMemo(() => {
    const languages = new Set<string>();
    tmdbSearchResults.forEach(movie => {
      if (movie.original_language) {
        languages.add(movie.original_language.toLowerCase());
      }
    });
    return Array.from(languages).sort();
  }, [tmdbSearchResults]);

  // Filter and sort results (defined early for use in grid navigation)
  const filteredAndSortedResults = useMemo(() => {
    let filtered = [...tmdbSearchResults];
    
    // Apply language filter
    if (filters.languages.length > 0) {
      filtered = filtered.filter(m => 
        m.original_language && filters.languages.includes(m.original_language.toLowerCase())
      );
    }
    
    // Apply rating filter
    if (filters.minRating > 0) {
      filtered = filtered.filter(m => (m.vote_average || 0) >= filters.minRating);
    }
    
    // Apply year range filter
    if (filters.yearRange.min || filters.yearRange.max) {
      filtered = filtered.filter(m => {
        if (!m.release_date) return false;
        const year = new Date(m.release_date).getFullYear();
        if (filters.yearRange.min && year < filters.yearRange.min) return false;
        if (filters.yearRange.max && year > filters.yearRange.max) return false;
        return true;
      });
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date':
          const dateA = a.release_date ? new Date(a.release_date).getTime() : 0;
          const dateB = b.release_date ? new Date(b.release_date).getTime() : 0;
          return dateB - dateA; // Newest first
        case 'popularity':
          return (b.popularity || 0) - (a.popularity || 0);
        case 'rating':
          return (b.vote_average || 0) - (a.vote_average || 0);
        default:
          return 0; // Already ranked by relevance
      }
    });
    
    return filtered;
  }, [tmdbSearchResults, filters, sortBy]);

  // Pad results with empty placeholders to fill complete rows
  const GRID_COLUMNS_XL = 6;
  const paddedResults = useMemo(() => {
    const results = [...filteredAndSortedResults];
    const remainder = results.length % GRID_COLUMNS_XL;
    if (remainder !== 0 && results.length > 0) {
      // Add empty placeholders to fill the row
      const needed = GRID_COLUMNS_XL - remainder;
      for (let i = 0; i < needed; i++) {
        results.push(null as any); // Add null as placeholder
      }
    }
    return results;
  }, [filteredAndSortedResults]);
  
  // Add TV class to body for CSS targeting
  useEffect(() => {
    if (isTV) {
      document.body.classList.add('tv-device');
    } else {
      document.body.classList.remove('tv-device');
    }
    return () => {
      document.body.classList.remove('tv-device');
    };
  }, [isTV]);

  // Restore scroll position when detail page closes (useLayoutEffect runs synchronously before paint)
  useLayoutEffect(() => {
    if (isTV && !showTVDetailsPage && lastScrollPosition > 0) {
      // Temporarily disable smooth scrolling to prevent animation
      const originalScrollBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = 'auto';
      
      // Restore scroll immediately before browser paints
      window.scrollTo(0, lastScrollPosition);
      document.documentElement.scrollTop = lastScrollPosition;
      document.body.scrollTop = lastScrollPosition;
      
      // Restore original scroll behavior after a frame
      requestAnimationFrame(() => {
        document.documentElement.style.scrollBehavior = originalScrollBehavior;
      });
    }
  }, [isTV, showTVDetailsPage, lastScrollPosition]);

  // Load navigation map for home page
  useEffect(() => {
    if (isTV) {
      loadPageMap('home', homePageMap);
      // Focus hero play button after a short delay
      setTimeout(() => {
        focusNode('hero-play-button');
      }, 100);
    }
  }, [isTV, loadPageMap, focusNode]);
  
  // Enable keyboard navigation
  useTVKeyboardNavigation();
  
  // Update hero buttons' down neighbor to point to first available row dynamically
  useEffect(() => {
    if (!isTV) return;
    
    // Retry mechanism to find the first row card (for continue watching that loads asynchronously)
    const findFirstRowCard = (attempt = 0): void => {
      // Increase max attempts for authenticated users (they may have continue watching that loads later)
      const maxAttempts = authenticated ? 30 : 15; // 6 seconds for authenticated, 3 seconds otherwise
      
      // Find the first available row by checking which rows are actually registered
      // This is dynamic and doesn't hardcode any specific row name
      let firstRowCardId: string | undefined;
      
      // Get all registered nodes from the engine
      const allNodes = engine.getAllNodes();
      
      // Find all row nodes, sorted by rowIndex
      const rowNodes = allNodes
        .filter(node => node.type === 'row')
        .sort((a, b) => (a.position.rowIndex || 0) - (b.position.rowIndex || 0));
      
      if (rowNodes.length > 0) {
        // Get the first row (lowest rowIndex)
        const firstRow = rowNodes[0];
        const firstRowId = firstRow.metadata?.rowId;
        
        if (firstRowId) {
          // Find the first card in this row
          const firstRowCards = allNodes
            .filter(node => 
              node.metadata?.rowId === firstRowId && 
              node.type === 'card' &&
              node.metadata?.cardIndex === 0
            );
          
          if (firstRowCards.length > 0) {
            firstRowCardId = firstRowCards[0].id;
          }
        }
      }
      
      // Update hero buttons to point down to first available row
      if (firstRowCardId) {
        console.log(`[HeroButton] Setting down neighbor to: ${firstRowCardId}`);
        updateNodeNeighbors('hero-play-button', {
          down: firstRowCardId
        });
        updateNodeNeighbors('hero-info-button', {
          down: firstRowCardId
        });
      } else if (attempt < maxAttempts) {
        // Retry if card not found yet (for async-loaded rows like continue watching)
        // Use longer delay for authenticated users
        const delay = authenticated && attempt < 10 ? 300 : 200;
        setTimeout(() => findFirstRowCard(attempt + 1), delay);
      } else {
        console.warn('[HeroButton] Failed to find first row card after', maxAttempts, 'attempts');
      }
    };
    
    // Start finding first row card after a small delay
    // Use longer initial delay for authenticated users (to wait for continue watching)
    const initialDelay = authenticated ? 500 : 200;
    const timeoutId = setTimeout(() => {
      findFirstRowCard();
    }, initialDelay);
    
    return () => clearTimeout(timeoutId);
  }, [
    isTV, 
    authenticated, 
    trendingMovies.length, 
    popularMovies.length, 
    availableLists.length,
    updateNodeNeighbors,
    engine
  ]);
  

  // Grid navigation for search results
  // Calculate grid rows based on padded results (but only count real movies for selection)
  const searchGridRows = Math.ceil(paddedResults.length / searchGridColumns);
  const { focusIndex: searchFocusIndex } = useTVGrid({
    columns: searchGridColumns,
    rows: searchGridRows,
    onSelect: (index) => {
      // Only handle selection for real movies (not placeholders)
      if (index >= 0 && index < paddedResults.length) {
        const movie = paddedResults[index];
        if (movie) {
          handleTMDBMovieClick(movie);
        }
      }
    },
    onBack: () => {
      setShowTMDBSearch(false);
      setTmdbSearchResults([]);
      setSearchQuery('');
      setFilters({ languages: [], minRating: 0, yearRange: {} });
      setSortBy('relevance');
    }
  });
  
  // Focus the selected card in search grid
  useEffect(() => {
    if (isTV && searchFocusIndex >= 0 && searchCardRefs.current[searchFocusIndex]) {
      searchCardRefs.current[searchFocusIndex]?.focus();
      searchCardRefs.current[searchFocusIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  }, [isTV, searchFocusIndex]);

  // Supabase automatically handles OAuth callback
  // No manual handling needed

  // Handle "Continue Watching" auto-resume from URL parameters
  useEffect(() => {
    console.log('🔍 Auto-resume effect triggered:', { 
      loading, 
      authenticated, 
      searchParams,
      playMovie: searchParams.playMovie,
      resumeFrom: searchParams.resumeFrom
    });
    
    // Don't run if still loading auth state
    if (loading) {
      console.log('⏭️ Skipping - still loading auth');
      return;
    }

    const handleAutoResume = async () => {
      const playMovieId = searchParams.playMovie;
      const resumeFrom = searchParams.resumeFrom;

      console.log('🔍 Search params:', { playMovieId, resumeFrom });

      if (!playMovieId) {
        console.log('⏭️ No playMovie parameter found');
        return;
      }

      // If not authenticated, still fetch movie details and open the modal/details
      if (!authenticated) {
        try {
          console.log('ℹ️ Not authenticated - opening details only');
          const tmdbRes = await fetch(getApiUrl('TMDB_MOVIE_DETAILS', playMovieId.toString()));
          const tmdbData = await tmdbRes.json();
          if (tmdbData.success && tmdbData.movie) {
            setSelectedTMDBMovie(tmdbData.movie);
            if (isTV) {
              setShowTVDetailsPage(true);
            } else {
              setIsModalOpen(true);
            }
          }
        } catch (err) {
          console.error('Failed to fetch movie details for playMovie (unauth):', err);
        }
        return;
      }

      console.log('🎬 Auto-resuming movie:', playMovieId, 'from position:', resumeFrom);

      // Show loading overlay immediately
      setIsLoadingStream(true);

      try {
        // Fetch movie details from TMDB
        const tmdbRes = await fetch(getApiUrl('TMDB_MOVIE_DETAILS', playMovieId.toString()));
        const tmdbData = await tmdbRes.json();

        if (!tmdbData.success || !tmdbData.movie) {
          console.error('Failed to fetch movie details for auto-resume');
          setIsLoadingStream(false);
          return;
        }

        const movie = tmdbData.movie;
        setSelectedTMDBMovie(movie);

        // Fetch watch history to get stream details
        const headers = getAuthHeaders()
        const watchHistoryRes = await fetch(getApiUrl('WATCH_HISTORY_RECENT'), {
          headers
        });
        const watchHistoryData = await watchHistoryRes.json();

        if (!watchHistoryData.success) {
          console.error('Failed to fetch watch history');
          setIsLoadingStream(false);
          return;
        }

        // Find the specific watch history entry
        const historyEntry = watchHistoryData.data.find((entry: any) => 
          entry.tmdb_id === playMovieId
        );

        if (!historyEntry || !historyEntry.stream_url) {
          console.error('No stream URL found in watch history');
          setIsLoadingStream(false);
          return;
        }

        console.log('✅ Found watch history entry:', historyEntry);

        // Set up the movie for playback
        // For FileStream, prioritize telegram_file_id; for GDrive, prioritize gdrive_file_id
        const fileIdValue = historyEntry.source_type === 'filestream' 
          ? (historyEntry.telegram_file_id || historyEntry.source_id)
          : historyEntry.source_type === 'gdrive'
            ? (historyEntry.gdrive_file_id || historyEntry.source_id)
            : historyEntry.source_id;
        
        setSelectedMovie({
          file_name: movie.title,
          title: movie.title,
          file_id: fileIdValue,
          file_size: 0,
          duration_seconds: historyEntry.total_duration_seconds || 0
        });

        // Set resume position
        if (resumeFrom && resumeFrom > 0) {
          setResumeFromPosition(resumeFrom);
        }

        // For FileStream, check if GDrive upgrade is available
        if (historyEntry.source_type === 'filestream' && fileIdValue) {
          console.log('🔄 Checking if GDrive upgrade available for FileStream movie:', fileIdValue);
          
          try {
            // Check if GDrive version is available
            const upgradeHeaders = {
              'Content-Type': 'application/json',
              ...getAuthHeaders()
            }
            const upgradeResponse = await fetch(getApiUrl('WATCH_HISTORY_CHECK_UPGRADE'), {
              method: 'POST',
              headers: upgradeHeaders,
              body: JSON.stringify({
                tmdb_id: playMovieId,
                telegram_file_id: fileIdValue
              })
            });
            
            const upgradeData = await upgradeResponse.json();
            
            if (upgradeData.success && upgradeData.upgrade_available) {
              // ✨ GDrive version available - upgrade!
              console.log('✨ GDrive upgrade available - switching to GDrive for better performance');
              
              // Update watch_history in backend (async, no wait)
              fetch(getApiUrl('WATCH_HISTORY_UPGRADE_TO_GDRIVE'), {
                method: 'POST',
                headers: upgradeHeaders,
                body: JSON.stringify({
                  tmdb_id: playMovieId,
                  gdrive_stream_url: upgradeData.gdrive_stream_url,
                  gdrive_file_id: upgradeData.gdrive_file_id
                })
              }).catch(err => console.error('Failed to upgrade watch history:', err));
              
              // Use GDrive URL
              setStreamUrl(upgradeData.gdrive_stream_url);
              setStreamSource('gdrive');
              setFileId(upgradeData.gdrive_file_id);
            } else {
              // ✅ Use existing FileStream URL (it's still valid!)
              console.log('✅ Using existing FileStream URL (GDrive upload still pending)');
              setStreamUrl(historyEntry.stream_url);
              setStreamSource('filestream');
              setFileId(fileIdValue);
            }
          } catch (error) {
            console.error('❌ Error checking upgrade:', error);
            // Fallback to FileStream URL
            console.log('⚠️ Falling back to FileStream URL');
            setStreamUrl(historyEntry.stream_url);
            setStreamSource('filestream');
            setFileId(fileIdValue);
          }
        } else {
          // For GDrive, use the stored URL (GDrive URLs are stable)
          setStreamUrl(historyEntry.stream_url);
          setStreamSource(historyEntry.source_type);
          setFileId(fileIdValue);
        }

        // Clear URL parameters using navigate
        navigate({ to: '/', search: { playMovie: undefined, resumeFrom: undefined } });

        // Loading overlay will be hidden when VideoPlayer calls onReady
      } catch (error) {
        console.error('Error auto-resuming movie:', error);
        setIsLoadingStream(false);
      }
    };

    handleAutoResume();
  }, [loading, authenticated, searchParams.playMovie, searchParams.resumeFrom]); // Run when auth or search params change

  // Load TMDB and MDBList movies on mount
  useEffect(() => {
    const fetchTMDBMovies = async () => {
      try {
        setLoadingTMDB(true);
        
        const [trendingRes, popularRes] = await Promise.all([
          fetch(getApiUrl('TMDB_TRENDING')),
          fetch(getApiUrl('TMDB_POPULAR')),
        ]);

        const [trendingData, popularData] = await Promise.all([
          trendingRes.json(),
          popularRes.json(),
        ]);

        if (trendingData.success && trendingData.results) {
          setTrendingMovies(trendingData.results);
          // Set first trending movie as hero
          if (trendingData.results.length > 0) {
            setHeroMovie(trendingData.results[0]);
          }
        }

        if (popularData.success && popularData.results) {
          setPopularMovies(popularData.results);
        }
      } catch (error) {
        console.error('Error fetching TMDB movies:', error);
      } finally {
        setLoadingTMDB(false);
      }
    };

    // Always fetch TMDB movies (for both authenticated and guest users)
    fetchTMDBMovies();
  }, []);

  // Load available MDBList categories
  useEffect(() => {
    const fetchAvailableLists = async () => {
      try {
        const response = await fetch(getApiUrl('MDBLIST_LISTS'));
        const data = await response.json();
        
        if (data.success && data.lists) {
          setAvailableLists(data.lists);
          console.log('📋 Loaded available lists:', data.lists);
        }
      } catch (error) {
        console.error('Error fetching available lists:', error);
      }
    };

    fetchAvailableLists();
  }, []);

  // Handle manual search from header
  const handleHeaderSearch = async (query: string) => {
    setBotResults([]);
    setShowBotResults(true);
    setStreamUrl(null);
    setSelectedMovie(null);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 90000); // 90 seconds for search

      const response = await fetch(getApiUrl('SEARCH'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      const data = await response.json();
      
      if (data.success) {
        setBotResults(data.movies || []);
      } else {
        console.error('Search failed:', data.error);
        alert(`Search failed: ${data.error}`);
      }
    } catch (error) {
      console.error('Search error:', error);
      if ((error as any)?.name === 'AbortError') {
        alert('Search request timed out. Please try again.');
      } else {
        alert(`Search error: ${(error as any)?.message || 'Unknown error'}`);
      }
    }
  };

  // Handle closing TV details page
  const handleCloseTVDetailsPage = useCallback(() => {
    setShowTVDetailsPage(false);
    setSelectedTMDBMovie(null);
    
    // Note: Scroll position restoration is handled in useLayoutEffect above
    // which runs synchronously when showTVDetailsPage changes to false
    
    // Restore focus after ensuring node is registered (for progressive rows)
    if (isTV && lastFocusedNodeId) {
      const nodeId = lastFocusedNodeId;
      
      // Function to check if node exists and restore focus with retry
      const tryRestoreFocus = (attempt: number = 0) => {
        const maxAttempts = 15; // Try for up to 3 seconds (15 * 200ms)
        
        // Check if node is registered by querying the engine
        const allNodes = engine.getAllNodes();
        const nodeExists = allNodes.some(node => node.id === nodeId);
        
        if (nodeExists) {
          // Node is registered, try to focus it with instant scroll (we already restored scroll position)
          focusNode(nodeId, true);
          
          // Verify focus was set
          setTimeout(() => {
            const focusedElement = document.activeElement;
            const isFocused = focusedElement && 
                             focusedElement !== document.body && 
                             (focusedElement.getAttribute('tabindex') !== null || 
                              focusedElement.tagName === 'BUTTON' ||
                              focusedElement.tagName === 'A' ||
                              focusedElement.classList.contains('tv-card') ||
                              focusedElement.classList.contains('movie-card'));
            
            if (isFocused) {
              // Focus was set successfully
              setLastFocusedNodeId(null);
              setLastScrollPosition(0); // Clear stored position
            } else if (attempt < maxAttempts) {
              // Node exists but focus didn't work, retry
              setTimeout(() => tryRestoreFocus(attempt + 1), 200);
            } else {
              console.warn(`[TVNavigation] Node ${nodeId} exists but focus failed after ${maxAttempts} attempts`);
              setLastFocusedNodeId(null);
              setLastScrollPosition(0);
            }
          }, 100);
        } else if (attempt < maxAttempts) {
          // Node not registered yet (for progressive rows that are still loading), retry
          setTimeout(() => tryRestoreFocus(attempt + 1), 200);
        } else {
          // Give up after max attempts
          console.warn(`[TVNavigation] Failed to restore focus to ${nodeId} - node not registered after ${maxAttempts} attempts`);
          setLastFocusedNodeId(null);
          setLastScrollPosition(0);
        }
      };
      
      // Start restoration after initial delay (scroll is already restored)
      setTimeout(() => {
        tryRestoreFocus(0);
      }, 200);
    }
  }, [isTV, lastFocusedNodeId, lastScrollPosition, focusNode, engine]);

  // Handle search bot for specific TMDB movie
  const handleSearchBotForMovie = useCallback(async (movieTitle: string): Promise<Movie[]> => {
    const response = await fetch(getApiUrl('SEARCH'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: movieTitle }),
    });

    const data = await response.json();
    if (data.success) {
      return data.movies || [];
    }
    return [];
  }, []);

  // Handle loading cancellation
  const handleCancelLoading = useCallback(() => {
    console.log('🚫 User cancelled loading');
    
    // Abort any ongoing fetch requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Clear timeout
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }
    
    // Reset all loading states
    setIsLoadingStream(false);
    setShowLoadingTimeout(false);
    setSelectedMovie(null);
    
    // Don't reset modal states - let user try again
  }, []);

  // Handle movie selection from bot results
  const handleMovieSelect = async (movie: Movie) => {
    setSelectedMovie(movie);
    setIsModalOpen(false); // Close modal (but keep selectedTMDBMovie for watch tracking)
    setShowBotResults(false);
    
    // Show loading overlay immediately
    setIsLoadingStream(true);
    setShowLoadingTimeout(false);
    
    // Create abort controller for this request
    abortControllerRef.current = new AbortController();
    
    // Set timeout warning after 45 seconds
    const warningTimeout = setTimeout(() => {
      setShowLoadingTimeout(true);
    }, 45000);
    
    // Set hard timeout after 90 seconds
    loadingTimeoutRef.current = setTimeout(() => {
      console.error('⏱️ Loading timeout - taking too long');
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      setIsLoadingStream(false);
      setShowLoadingTimeout(false);
      alert('Loading is taking too long.clear');
      setSelectedMovie(null);
    }, 90000);

    try {
      console.log(`🎬 Starting stream for movie: ${movie.title} (ID: ${movie.id})`);
      
      // Use callbackData directly if available, otherwise fall back to movie.id
      const movieId = movie.callbackData || movie.id;
      console.log(`🔑 Using movieId: ${movieId} (${movie.callbackData ? 'callbackData' : 'button position'})`);
      
      // First API call - get stream setup
      const response = await fetch(getApiUrl('STREAM_MOVIE'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ movieId: movieId, searchQuery: movie.title }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('📦 Backend response:', data);
      
      if (!data.success) {
        throw new Error(data.error || 'Stream setup failed');
      }
      
      if (data.success) {
        console.log(`✅ Stream setup completed, file ID: ${data.fileId}`);
        console.log(`🔗 Stream URL: ${data.streamUrl}`);
        
        // Check if streamUrl is valid
        if (!data.streamUrl) {
          console.error('❌ No streamUrl in response:', data);
          throw new Error('No stream URL received from backend');
        }
        
        // Second API call - get actual stream URL
        // Ensure we have a full URL
        const fullStreamUrl = data.streamUrl.startsWith('http') 
          ? data.streamUrl 
          : createApiUrl(data.streamUrl);
        
        console.log(`🔗 Fetching from: ${fullStreamUrl}`);
        
        const driveResponse = await fetch(fullStreamUrl, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          signal: abortControllerRef.current.signal,
        });
        
        if (!driveResponse.ok) {
          throw new Error(`Stream request failed: ${driveResponse.status}`);
        }
        
        const driveData = await driveResponse.json();
        
        if (driveData.success && driveData.streamUrl) {
          console.log(`✅ Stream ready!`);
          
          // Clear timeouts
          clearTimeout(warningTimeout);
          if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
          }
          
          // Set stream data
          setStreamUrl(driveData.streamUrl);
          setStreamSource(driveData.source || 'gdrive');
          setFileId(driveData.fileId || data.fileId);
          
          if (driveData.uploadJobId) {
            setUploadJobId(driveData.uploadJobId);
          } else {
            setUploadJobId(null);
          }
          
          if (driveData.uploadingInBackground && driveData.progressCheckUrl) {
            setProgressCheckUrl(driveData.progressCheckUrl);
            setUploadingInBackground(true);
          } else {
            setProgressCheckUrl(null);
            setUploadingInBackground(false);
          }
          
          // Hide loading overlay
          setIsLoadingStream(false);
          setShowLoadingTimeout(false);
        } else {
          throw new Error(driveData.error || 'No stream URL received');
        }
      } else {
        throw new Error(data.error || 'Stream setup failed');
      }
    } catch (error: any) {
      // Clear timeouts
      clearTimeout(warningTimeout);
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
      
      // Hide loading
      setIsLoadingStream(false);
      setShowLoadingTimeout(false);
      
      // Handle abort vs real errors
      if (error.name === 'AbortError') {
        console.log('🚫 Request was cancelled by user');
        // Don't show alert for user-initiated cancellation
      } else {
        console.error('Streaming error:', error);
        alert(`Streaming error: ${error?.message || 'Unknown error'}`);
      }
      
      setSelectedMovie(null);
    }
  };

  // Handle TMDB search from header (live as user types)
  const handleTMDBSearch = async (query: string, page: number = 1, append: boolean = false) => {
    // Cancel any in-flight search request
    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
    }
    
    // If query is empty, go back to home view
    if (!query.trim()) {
      setShowTMDBSearch(false);
      setTmdbSearchResults([]);
      setSearchQuery('');
      setIsSearching(false);
      setSearchProgress(null);
      setCurrentPage(1);
      setHasMore(false);
      return;
    }
    
    // Reset pagination if new query
    if (!append) {
      setSearchQuery(query);
      setShowTMDBSearch(true);
      setStreamUrl(null);
      setSelectedMovie(null);
      setShowBotResults(false);
      setTmdbSearchResults([]);
      setCurrentPage(1);
      setHasMore(false);
    }
    
    setIsSearching(true);
    setSearchProgress({ phase: 'searching', message: 'Searching movies...' });
    
    // Create new abort controller for this search
    const abortController = new AbortController();
    searchAbortControllerRef.current = abortController;
    
    try {
      const response = await fetch(getApiUrl('TMDB_SEARCH', undefined, { query, page: page.toString() }), {
        signal: abortController.signal
      });
      
      // Check if request was aborted
      if (abortController.signal.aborted) {
        return;
      }
      
      const data = await response.json();
      
      // Check again if request was aborted after async operation
      if (abortController.signal.aborted) {
        return;
      }
      
      if (data.success && data.results) {
        if (append) {
          setTmdbSearchResults(prev => {
            const newResults = [...prev, ...data.results];
            return newResults;
          });
        } else {
          setTmdbSearchResults(data.results);
        }
        setCurrentPage(data.page || page);
        setHasMore(data.hasMore || false);
        setTotalResults(data.totalResults || data.results.length);
      } else {
        if (!append) {
          setTmdbSearchResults([]);
        }
      }
    } catch (error: any) {
      // Ignore abort errors
      if (error.name === 'AbortError') {
        return;
      }
      console.error('TMDB search error:', error);
      if (!append) {
        setTmdbSearchResults([]);
      }
    } finally {
      // Only update loading state if this request wasn't aborted
      if (!abortController.signal.aborted) {
        setIsSearching(false);
        setSearchProgress(null);
      }
    }
  };



  const handleLogoClick = useCallback(() => {
    // Reset to home state
    setStreamUrl(null);
    setSelectedMovie(null);
    setBotResults([]);
    setShowBotResults(false);
    setIsModalOpen(false);
    setSelectedTMDBMovie(null);
    setShowTMDBSearch(false);
    setTmdbSearchResults([]);
    setSearchQuery('');
    setIsSearching(false);
    setSearchProgress(null);
    setFilters({ languages: [], minRating: 0, yearRange: {} });
    setSortBy('relevance');
    setCurrentPage(1);
    setHasMore(false);
    setTotalResults(0);
    setResumeFromPosition(undefined); // Clear resume position
  }, []);

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#141414]">
        <div className="loading-spinner"></div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-[#141414]">
      {/* Loading Overlay */}
      <LoadingOverlay
        isLoading={isLoadingStream}
        onCancel={handleCancelLoading}
        showTimeout={showLoadingTimeout}
      />
      
      {/* Header - Hide on TV when details page is shown */}
      {!(isTV && showTVDetailsPage) && (
        <Header 
          onSearch={handleTMDBSearch}
          onLogoClick={handleLogoClick}
          onMovieClick={handleTMDBMovieClick}
        />
      )}

      {/* Main content - Hide on TV when details page is shown */}
      {!(isTV && showTVDetailsPage) && (
        <main id="main-content" className="relative" role="main">
        {/* Show video player if streaming */}
        {streamUrl && selectedMovie ? (
          <div className="pt-24 lg:pt-28 px-6 sm:px-8 lg:px-16 xl:px-20 pb-12">
            <VideoPlayer 
              streamUrl={streamUrl} 
              movieTitle={selectedMovie.title}
              progressCheckUrl={progressCheckUrl || undefined}
              uploadingInBackground={uploadingInBackground}
              uploadJobId={uploadJobId || undefined}
              source={streamSource}
              fileId={fileId || undefined}
              onBack={handleLogoClick}
              onReady={() => setIsLoadingStream(false)}
              resumeFrom={resumeFromPosition}
              tmdbId={selectedTMDBMovie?.id}
              posterPath={selectedTMDBMovie?.poster_path || undefined}
              backdropPath={selectedTMDBMovie?.backdrop_path || undefined}
              overview={selectedTMDBMovie?.overview}
              releaseDate={selectedTMDBMovie?.release_date}
              voteAverage={selectedTMDBMovie?.vote_average}
            />
          </div>
        ) : showTMDBSearch ? (
          // Show TMDB search results as movie cards
          <div className="pt-24 lg:pt-28 px-6 sm:px-8 lg:px-16 xl:px-20 pb-12">
            <div className="mb-6">
              <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">
                Search Results for "{searchQuery}"
              </h1>
              {searchProgress && (
                <div className="mb-4">
                  <p className="text-gray-400 text-sm mb-2">{searchProgress.message}</p>
                  {searchProgress.progress !== undefined && searchProgress.total !== undefined && (
                    <div className="w-full bg-white/10 rounded-full h-2">
                      <div 
                        className="bg-red-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(searchProgress.progress / searchProgress.total) * 100}%` }}
                      />
                    </div>
                  )}
                </div>
              )}
              {!isSearching && (
                <p className="text-gray-400">
                  Found {totalResults || filteredAndSortedResults.length} movie{(totalResults || filteredAndSortedResults.length) !== 1 ? 's' : ''}
                  {filters.languages.length > 0 || filters.minRating > 0 || filters.yearRange.min || filters.yearRange.max ? 
                    ` (${filteredAndSortedResults.length} after filters)` : ''}
                </p>
              )}
            </div>
            
            {/* Search Filters */}
            {tmdbSearchResults.length > 0 && (
              <SearchFilters
                onFilterChange={setFilters}
                onSortChange={setSortBy}
                availableLanguages={availableLanguages}
                currentFilters={filters}
                currentSort={sortBy}
              />
            )}
            
            {isSearching && tmdbSearchResults.length === 0 ? (
              // Skeleton loader for search results
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 lg:gap-6">
                {[...Array(12)].map((_, index) => (
                  <div key={index} className="relative aspect-[2/3] bg-gray-800 rounded-lg overflow-hidden">
                    <Skeleton className="w-full h-full rounded-lg bg-gray-700/50 animate-pulse-slow" />
                  </div>
                ))}
              </div>
            ) : filteredAndSortedResults.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 lg:gap-6">
                {paddedResults.map((movie, index) => {
                  // If it's a placeholder (null), render empty div
                  if (!movie) {
                    return (
                      <div 
                        key={`placeholder-${index}`} 
                        className="relative aspect-[2/3] bg-transparent"
                        aria-hidden="true"
                      />
                    );
                  }
                  
                  return (
                    <SearchResultMovieCard
                      key={movie.id}
                      movie={movie}
                      onClick={() => handleTMDBMovieClick(movie)}
                      isFocused={isTV && searchFocusIndex === index}
                      tabIndex={isTV ? (searchFocusIndex === index ? 0 : -1) : undefined}
                      ref={(el) => {
                        searchCardRefs.current[index] = el;
                      }}
                    />
                  );
                })}
              </div>
            ) : tmdbSearchResults.length > 0 ? (
              <div className="text-center py-20">
                <p className="text-gray-400 text-lg">No movies match your filters</p>
                <p className="text-gray-500 text-sm mt-2">Try adjusting your filter criteria</p>
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-gray-400 text-lg">No movies found for "{searchQuery}"</p>
                <p className="text-gray-500 text-sm mt-2">Try a different search term</p>
              </div>
            )}
          </div>
        ) : showBotResults ? (
          // Show bot results from manual search (legacy)
          <div className="pt-24 lg:pt-28 px-6 sm:px-8 lg:px-16 xl:px-20 pb-12">
            <div className="max-w-7xl mx-auto">
              <MovieList 
                movies={botResults}
                onMovieSelect={handleMovieSelect}
                selectedMovie={selectedMovie}
              />
            </div>
          </div>
        ) : (
          // Show Netflix-style TMDB movie browse
          <>
            {/* Hero billboard */}
            {loadingTMDB && !heroMovie ? (
              <HeroBillboardSkeleton />
            ) : heroMovie ? (
              <HeroBillboard
                movie={heroMovie}
                allMovies={{
                  trending: trendingMovies,
                  popular: popularMovies,
                  lists: availableLists
                }}
                onPlay={handleTMDBMovieClick}
              />
            ) : null}

            {/* Movie rows */}
            <div className={`relative ${heroMovie ? '-mt-16 lg:-mt-20' : 'mt-0'} z-10 pb-20 lg:pb-24 ${isTV ? 'px-4' : 'px-6 sm:px-8 lg:px-16 xl:px-20'}`}>
              {/* Continue Watching - Only for authenticated users */}
              {authenticated && <RecentlyWatchedRow />}
              
              <MovieRow
                title="Trending Now"
                movies={trendingMovies}
                onMovieClick={handleInstantPlay}
                onMovieInfo={handleTMDBMovieClick}
                loading={loadingTMDB}
                rowIndex={authenticated ? 1 : 0}
                previousRowId={authenticated ? 'continue-watching' : undefined}
                nextRowId="popular"
                upTarget="hero-play-button"
              />
              
              <MovieRow
                title="Popular Movies"
                movies={popularMovies}
                onMovieClick={handleInstantPlay}
                onMovieInfo={handleTMDBMovieClick}
                loading={loadingTMDB}
                rowIndex={authenticated ? 2 : 1}
                previousRowId="trending"
                nextRowId="browse-by-language"
                upTarget="hero-play-button"
              />

              {/* Browse by Language Row */}
              <CategoryRow
                title="Browse by Language"
                apiEndpoint={API_ENDPOINTS.MDBLIST_CATEGORIES_LANGUAGES}
                type="language"
                rowIndex={authenticated ? 3 : 2}
                previousRowId="popular"
                nextRowId="browse-by-genre"
                upTarget="hero-play-button"
              />

              {/* Browse by Genre Row */}
              <CategoryRow
                title="Browse by Genre"
                apiEndpoint={API_ENDPOINTS.MDBLIST_CATEGORIES_GENRES}
                type="genre"
                rowIndex={authenticated ? 4 : 3}
                previousRowId="browse-by-language"
                nextRowId={availableLists.length > 0 ? availableLists[0]?.id : undefined}
                upTarget="hero-play-button"
              />

              {/* Dynamic MDBList Movie Rows */}
              {availableLists.map((list, index) => (
                <ProgressiveMovieRow
                  key={list.id}
                  title={list.displayName}
                  apiEndpoint={getMDBListUrl(list.id)}
                  onMovieClick={handleInstantPlay}
                  onMovieInfo={handleTMDBMovieClick}
                  rowIndex={authenticated ? 5 + index : 4 + index}
                  previousRowId={index === 0 ? 'browse-by-genre' : availableLists[index - 1]?.id}
                  nextRowId={index < availableLists.length - 1 ? availableLists[index + 1]?.id : undefined}
                  upTarget="hero-play-button"
                />
              ))}
            </div>
          </>
        )}

        {/* Loading overlay - removed since we don't use isLoading state anymore */}
        </main>
      )}

      {/* Movie detail modal (Desktop/Mobile) */}
      {!isTV && (
        <MovieModal
          movie={selectedTMDBMovie}
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            // Don't clear selectedTMDBMovie here - keep it for watch tracking
            // It will be cleared when user goes back to home via handleLogoClick
          }}
          onSearchBot={handleSearchBotForMovie}
          onSelectVersion={handleMovieSelect}
          onMovieClick={handleTMDBMovieClick}
        />
      )}

      {/* TV Movie Details Page (Android TV) */}
      {isTV && showTVDetailsPage && selectedTMDBMovie && (
        <TVMovieDetailsPage
          movie={selectedTMDBMovie}
          onBack={handleCloseTVDetailsPage}
          onSearchBot={handleSearchBotForMovie}
          onSelectVersion={handleMovieSelect}
          onMovieClick={handleTMDBMovieClick}
        />
      )}
    </div>
  );
}
